'use client'

import { useAuth } from '@/hooks/useAuth'
import { signOut } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import {
	LogIn,
	LogOut,
	Settings,
	CreditCard,
	LayoutDashboard,
	Stamp,
	Info,
	Building2,
	Newspaper,
	Mail,
	ShieldCheck,
	FileText,
	Undo2,
	FileSignature,
	Scale,
	Cookie,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { openConsentSettings } from '@/components/consent/cookie-banner'

// The company + legal pages, reusing the footer's own labels so the two listings cannot drift. Inside the
// app there is no footer, so this submenu is the only way to reach them (and, for the consent settings,
// the Art. 7(3) GDPR way back out of consent).
const COMPANY_PAGES = [
	{ href: '/about', icon: Building2, key: 'about' },
	{ href: '/blog', icon: Newspaper, key: 'blog' },
	{ href: '/contact', icon: Mail, key: 'contact' },
] as const

const LEGAL_PAGES = [
	{ href: '/privacy', icon: ShieldCheck, key: 'privacy' },
	{ href: '/terms', icon: FileText, key: 'terms' },
	{ href: '/widerruf', icon: Undo2, key: 'withdrawal' },
	{ href: '/dpa', icon: FileSignature, key: 'dpa' },
	{ href: '/imprint', icon: Scale, key: 'imprint' },
] as const

export function UserMenu() {
	const { isAuthenticated, isLoading, user } = useAuth()
	const t = useTranslations('userMenu')
	const tNav = useTranslations('nav')
	const tFooter = useTranslations('footer')
	const tConsent = useTranslations('consent')

	if (isLoading) {
		return <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
	}

	if (!isAuthenticated) {
		return (
			<Link href="/auth/signin">
				<Button variant="default" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
					<LogIn className="mr-2 h-4 w-4" />
					{tNav('signIn')}
				</Button>
			</Link>
		)
	}

	const initials = user?.name
		? user.name
				.split(' ')
				.map((n) => n[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		: user?.email?.[0]?.toUpperCase() || '?'

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" className="relative h-8 w-8 rounded-full">
					<Avatar className="h-8 w-8">
						<AvatarImage src={user?.image || undefined} alt={user?.name || ''} />
						<AvatarFallback className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-xs">
							{initials}
						</AvatarFallback>
					</Avatar>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-56" align="end">
				<div className="flex items-center gap-2 p-2">
					<div className="flex min-w-0 flex-col space-y-0.5">
						{user?.name && <p className="truncate text-sm font-medium">{user.name}</p>}
						<p className="truncate text-xs text-muted-foreground">{user?.email}</p>
					</div>
				</div>
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<Link href="/dashboard/chat">
						<LayoutDashboard className="mr-2 h-4 w-4" />
						{t('dashboard')}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link href="/dashboard/settings/branding">
						<Stamp className="mr-2 h-4 w-4" />
						{t('branding')}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link href="/dashboard/settings/billing">
						<CreditCard className="mr-2 h-4 w-4" />
						{t('billing')}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link href="/dashboard/settings/account">
						<Settings className="mr-2 h-4 w-4" />
						{t('settings')}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuSub>
					<DropdownMenuSubTrigger>
						<Info className="mr-2 h-4 w-4" />
						{t('about')}
					</DropdownMenuSubTrigger>
					<DropdownMenuPortal>
						<DropdownMenuSubContent className="w-52">
							<DropdownMenuLabel className="text-xs text-muted-foreground">{tFooter('company')}</DropdownMenuLabel>
							{COMPANY_PAGES.map(({ href, icon: Icon, key }) => (
								<DropdownMenuItem key={href} asChild>
									<Link href={href}>
										<Icon className="mr-2 h-4 w-4" />
										{tFooter(key)}
									</Link>
								</DropdownMenuItem>
							))}
							<DropdownMenuSeparator />
							<DropdownMenuLabel className="text-xs text-muted-foreground">{tFooter('legal')}</DropdownMenuLabel>
							{LEGAL_PAGES.map(({ href, icon: Icon, key }) => (
								<DropdownMenuItem key={href} asChild>
									<Link href={href}>
										<Icon className="mr-2 h-4 w-4" />
										{tFooter(key)}
									</Link>
								</DropdownMenuItem>
							))}
							<DropdownMenuItem onClick={openConsentSettings}>
								<Cookie className="mr-2 h-4 w-4" />
								{tConsent('footerLink')}
							</DropdownMenuItem>
						</DropdownMenuSubContent>
					</DropdownMenuPortal>
				</DropdownMenuSub>
				<DropdownMenuItem onClick={() => signOut({ callbackUrl: '/' })}>
					<LogOut className="mr-2 h-4 w-4" />
					{t('signOut')}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
